import { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  Users,
  Building2,
  Eye,
  MessageSquare,
  Target,
  Globe,
  Activity,
  Flame,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
} from 'lucide-react';
import api from '../../services/api';

export default function AdminAnalytics() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [therapeuticAreas, setTherapeuticAreas] = useState(null);
  const [matchValidation, setMatchValidation] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [geography, setGeography] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [overviewRes, taRes, matchRes, leaderRes, geoRes] = await Promise.all([
        api.get('/admin/analytics/overview').catch(() => ({ data: { data: null } })),
        api.get('/admin/analytics/therapeutic-areas').catch(() => ({ data: { data: null } })),
        api.get('/admin/analytics/match-validation').catch(() => ({ data: { data: null } })),
        api.get('/admin/analytics/leaderboard').catch(() => ({ data: { data: null } })),
        api.get('/admin/analytics/geography').catch(() => ({ data: { data: null } })),
      ]);

      setOverview(overviewRes.data?.data);
      setTherapeuticAreas(taRes.data?.data);
      setMatchValidation(matchRes.data?.data);
      setLeaderboard(leaderRes.data?.data);
      setGeography(geoRes.data?.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">BD Analytics</h1>
        <p className="text-gray-600">Comprehensive business development intelligence</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-200 pb-2 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'funnel', label: 'BD Funnel', icon: TrendingUp },
          { id: 'therapeutic', label: 'Therapeutic Areas', icon: Activity },
          { id: 'leaderboard', label: 'Leaderboard', icon: Target },
          { id: 'geography', label: 'Geography', icon: Globe },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-red-100 text-red-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && overview && (
        <div className="space-y-6">
          {/* Platform Health Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              label="Total Sellers"
              value={overview.platformHealth?.total_sellers || 0}
              icon={Building2}
              color="purple"
            />
            <MetricCard
              label="Total Buyers"
              value={overview.platformHealth?.total_buyers || 0}
              icon={Users}
              color="blue"
            />
            <MetricCard
              label="New Users (7d)"
              value={overview.platformHealth?.new_users_7d || 0}
              icon={TrendingUp}
              color="green"
              trend={overview.platformHealth?.new_users_7d > 0 ? 'up' : null}
            />
            <MetricCard
              label="Active Sellers"
              value={overview.platformHealth?.active_sellers_30d || 0}
              icon={Activity}
              color="orange"
              subtext="Last 30 days"
            />
          </div>

          {/* BD Funnel Summary */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">BD Funnel (30 Days)</h2>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <FunnelStep
                label="Profile Views"
                value={overview.bdFunnel?.profile_views_30d || 0}
                icon={Eye}
                color="blue"
              />
              <FunnelStep
                label="Follows"
                value={overview.bdFunnel?.follows_30d || 0}
                icon={Users}
                color="purple"
                conversionRate={calculateConversion(
                  overview.bdFunnel?.follows_30d,
                  overview.bdFunnel?.profile_views_30d
                )}
              />
              <FunnelStep
                label="Messages"
                value={overview.bdFunnel?.messages_30d || 0}
                icon={MessageSquare}
                color="green"
                conversionRate={calculateConversion(
                  overview.bdFunnel?.messages_30d,
                  overview.bdFunnel?.follows_30d
                )}
              />
              <FunnelStep
                label="Hot Leads"
                value={overview.bdFunnel?.hot_leads || 0}
                icon={Flame}
                color="orange"
              />
              <FunnelStep
                label="Intro Requests"
                value={overview.bdFunnel?.intro_requests_30d || 0}
                icon={Target}
                color="red"
              />
            </div>
          </div>

          {/* Daily Trend */}
          {overview.dailyTrend && overview.dailyTrend.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Daily Activity (Last 7 Days)</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Date</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">Views</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">Messages</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">New Users</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.dailyTrend.map((day, idx) => (
                      <tr key={idx} className="border-b border-gray-50">
                        <td className="py-2 px-3 text-gray-900">
                          {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </td>
                        <td className="py-2 px-3 text-right font-medium text-blue-600">{day.views || 0}</td>
                        <td className="py-2 px-3 text-right font-medium text-green-600">{day.messages || 0}</td>
                        <td className="py-2 px-3 text-right font-medium text-purple-600">{day.new_users || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* BD Funnel Tab */}
      {activeTab === 'funnel' && overview && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">BD Conversion Funnel</h2>
            <div className="space-y-4">
              <FunnelBar
                label="Profile Views"
                value={overview.bdFunnel?.profile_views_30d || 0}
                maxValue={overview.bdFunnel?.profile_views_30d || 1}
                color="blue"
              />
              <FunnelBar
                label="Follows"
                value={overview.bdFunnel?.follows_30d || 0}
                maxValue={overview.bdFunnel?.profile_views_30d || 1}
                color="purple"
                conversionRate={calculateConversion(
                  overview.bdFunnel?.follows_30d,
                  overview.bdFunnel?.profile_views_30d
                )}
              />
              <FunnelBar
                label="Messages Sent"
                value={overview.bdFunnel?.messages_30d || 0}
                maxValue={overview.bdFunnel?.profile_views_30d || 1}
                color="green"
                conversionRate={calculateConversion(
                  overview.bdFunnel?.messages_30d,
                  overview.bdFunnel?.profile_views_30d
                )}
              />
              <FunnelBar
                label="Introduction Requests"
                value={overview.bdFunnel?.intro_requests_30d || 0}
                maxValue={overview.bdFunnel?.profile_views_30d || 1}
                color="red"
                conversionRate={calculateConversion(
                  overview.bdFunnel?.intro_requests_30d,
                  overview.bdFunnel?.profile_views_30d
                )}
              />
            </div>
          </div>

          {/* Key Insights */}
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Conversion Insights</h3>
              <div className="space-y-3">
                <InsightRow
                  label="View-to-Follow Rate"
                  value={`${calculateConversion(overview.bdFunnel?.follows_30d, overview.bdFunnel?.profile_views_30d)}%`}
                  benchmark="Industry avg: 5-10%"
                />
                <InsightRow
                  label="Follow-to-Message Rate"
                  value={`${calculateConversion(overview.bdFunnel?.messages_30d, overview.bdFunnel?.follows_30d)}%`}
                  benchmark="Target: >20%"
                />
                <InsightRow
                  label="Message-to-Intro Rate"
                  value={`${calculateConversion(overview.bdFunnel?.intro_requests_30d, overview.bdFunnel?.messages_30d)}%`}
                  benchmark="Target: >10%"
                />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Platform Health</h3>
              <div className="space-y-3">
                <InsightRow
                  label="Active Rate (Sellers)"
                  value={`${calculateConversion(overview.platformHealth?.active_sellers_30d, overview.platformHealth?.total_sellers)}%`}
                  benchmark="Target: >30%"
                />
                <InsightRow
                  label="Average Messages/Conversation"
                  value={overview.bdFunnel?.avg_messages_per_conversation?.toFixed(1) || '0'}
                  benchmark="Healthy: >3"
                />
                <InsightRow
                  label="Hot Lead Ratio"
                  value={`${calculateConversion(overview.bdFunnel?.hot_leads, overview.platformHealth?.total_buyers)}%`}
                  benchmark="Of all buyers"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Therapeutic Areas Tab */}
      {activeTab === 'therapeutic' && therapeuticAreas && (
        <div className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Seller Distribution */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Seller Distribution by TA</h2>
              <div className="space-y-3">
                {therapeuticAreas.sellerDistribution?.slice(0, 10).map((ta, idx) => (
                  <DistributionBar
                    key={idx}
                    label={ta.therapeutic_area}
                    value={parseInt(ta.count)}
                    maxValue={parseInt(therapeuticAreas.sellerDistribution[0]?.count) || 1}
                    color="purple"
                  />
                ))}
                {(!therapeuticAreas.sellerDistribution || therapeuticAreas.sellerDistribution.length === 0) && (
                  <p className="text-gray-500 text-center py-4">No seller data available</p>
                )}
              </div>
            </div>

            {/* Buyer Distribution */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Buyer Interest by TA</h2>
              <div className="space-y-3">
                {therapeuticAreas.buyerDistribution?.slice(0, 10).map((ta, idx) => (
                  <DistributionBar
                    key={idx}
                    label={ta.therapeutic_area}
                    value={parseInt(ta.count)}
                    maxValue={parseInt(therapeuticAreas.buyerDistribution[0]?.count) || 1}
                    color="blue"
                  />
                ))}
                {(!therapeuticAreas.buyerDistribution || therapeuticAreas.buyerDistribution.length === 0) && (
                  <p className="text-gray-500 text-center py-4">No buyer data available</p>
                )}
              </div>
            </div>
          </div>

          {/* Supply-Demand Analysis */}
          {therapeuticAreas.supplyDemand && therapeuticAreas.supplyDemand.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Supply vs Demand Analysis</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Therapeutic Area</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">Sellers</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">Buyer Interest</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {therapeuticAreas.supplyDemand.slice(0, 15).map((row, idx) => {
                      const balance = (parseInt(row.buyer_interest) || 0) - (parseInt(row.seller_count) || 0);
                      return (
                        <tr key={idx} className="border-b border-gray-50">
                          <td className="py-2 px-3 text-gray-900">{row.therapeutic_area}</td>
                          <td className="py-2 px-3 text-right font-medium text-purple-600">{row.seller_count}</td>
                          <td className="py-2 px-3 text-right font-medium text-blue-600">{row.buyer_interest}</td>
                          <td className={`py-2 px-3 text-right font-medium ${
                            balance > 0 ? 'text-green-600' : balance < 0 ? 'text-red-600' : 'text-gray-500'
                          }`}>
                            {balance > 0 ? '+' : ''}{balance}
                            {balance > 0 && <span className="text-xs ml-1">undersupply</span>}
                            {balance < 0 && <span className="text-xs ml-1">oversupply</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && leaderboard && (
        <div className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Top Sellers */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Sellers (by Views)</h2>
              <div className="space-y-3">
                {leaderboard.topSellers?.map((seller, idx) => (
                  <LeaderboardRow
                    key={seller.id}
                    rank={idx + 1}
                    name={seller.company_name}
                    primaryStat={`${seller.view_count} views`}
                    secondaryStat={`${seller.unique_viewers} unique`}
                    highlight={idx === 0}
                  />
                ))}
                {(!leaderboard.topSellers || leaderboard.topSellers.length === 0) && (
                  <p className="text-gray-500 text-center py-4">No seller activity yet</p>
                )}
              </div>
            </div>

            {/* Most Active Buyers */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Most Active Buyers</h2>
              <div className="space-y-3">
                {leaderboard.activeBuyers?.map((buyer, idx) => (
                  <LeaderboardRow
                    key={buyer.id}
                    rank={idx + 1}
                    name={buyer.company_name}
                    primaryStat={`${buyer.profiles_viewed} profiles`}
                    secondaryStat={`${buyer.follows_count} follows`}
                    highlight={idx === 0}
                  />
                ))}
                {(!leaderboard.activeBuyers || leaderboard.activeBuyers.length === 0) && (
                  <p className="text-gray-500 text-center py-4">No buyer activity yet</p>
                )}
              </div>
            </div>
          </div>

          {/* Hot Leads */}
          {leaderboard.hotLeads && leaderboard.hotLeads.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Flame className="w-5 h-5 text-orange-500" />
                Hottest Leads Platform-wide
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Buyer</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Interested In</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">Interest Score</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">Last Activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.hotLeads.slice(0, 10).map((lead, idx) => (
                      <tr key={idx} className="border-b border-gray-50">
                        <td className="py-2 px-3 font-medium text-gray-900">{lead.buyer_name}</td>
                        <td className="py-2 px-3 text-gray-600">{lead.seller_name}</td>
                        <td className="py-2 px-3 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            lead.interest_score >= 80 ? 'bg-orange-100 text-orange-700' :
                            lead.interest_score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {lead.interest_score}%
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right text-gray-500">
                          {formatTimeAgo(lead.last_activity)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Geography Tab */}
      {activeTab === 'geography' && geography && (
        <div className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Seller Geography */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Sellers by Location</h2>
              <div className="space-y-3">
                {geography.sellerLocations?.slice(0, 10).map((loc, idx) => (
                  <DistributionBar
                    key={idx}
                    label={loc.country || 'Unknown'}
                    value={parseInt(loc.count)}
                    maxValue={parseInt(geography.sellerLocations[0]?.count) || 1}
                    color="purple"
                  />
                ))}
                {(!geography.sellerLocations || geography.sellerLocations.length === 0) && (
                  <p className="text-gray-500 text-center py-4">No location data available</p>
                )}
              </div>
            </div>

            {/* Buyer Geography */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Buyers by Location</h2>
              <div className="space-y-3">
                {geography.buyerLocations?.slice(0, 10).map((loc, idx) => (
                  <DistributionBar
                    key={idx}
                    label={loc.country || 'Unknown'}
                    value={parseInt(loc.count)}
                    maxValue={parseInt(geography.buyerLocations[0]?.count) || 1}
                    color="blue"
                  />
                ))}
                {(!geography.buyerLocations || geography.buyerLocations.length === 0) && (
                  <p className="text-gray-500 text-center py-4">No location data available</p>
                )}
              </div>
            </div>
          </div>

          {/* Cross-border Interest */}
          {geography.crossBorderInterest && geography.crossBorderInterest.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Cross-border BD Interest</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Buyer Location</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Seller Location</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">Views</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">Interest Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {geography.crossBorderInterest.slice(0, 10).map((row, idx) => (
                      <tr key={idx} className="border-b border-gray-50">
                        <td className="py-2 px-3 text-gray-900">{row.buyer_location || 'Unknown'}</td>
                        <td className="py-2 px-3 text-gray-600">{row.seller_location || 'Unknown'}</td>
                        <td className="py-2 px-3 text-right font-medium text-blue-600">{row.view_count}</td>
                        <td className="py-2 px-3 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            row.view_count >= 50 ? 'bg-green-100 text-green-700' :
                            row.view_count >= 20 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {row.view_count >= 50 ? 'High' : row.view_count >= 20 ? 'Medium' : 'Low'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Helper Components
function MetricCard({ label, value, icon: Icon, color, trend, subtext }) {
  const colorClasses = {
    purple: 'bg-purple-100 text-purple-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600',
    red: 'bg-red-100 text-red-600',
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend && (
          <span className={`flex items-center text-xs font-medium ${
            trend === 'up' ? 'text-green-600' : 'text-red-600'
          }`}>
            {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-600">{label}</p>
      {subtext && <p className="text-xs text-gray-400">{subtext}</p>}
    </div>
  );
}

function FunnelStep({ label, value, icon: Icon, color, conversionRate }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600',
    red: 'bg-red-100 text-red-600',
  };

  return (
    <div className="text-center">
      <div className={`w-12 h-12 mx-auto rounded-lg ${colorClasses[color]} flex items-center justify-center mb-2`}>
        <Icon className="w-6 h-6" />
      </div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-600">{label}</p>
      {conversionRate !== undefined && (
        <p className="text-xs text-gray-400 mt-1">{conversionRate}% conv.</p>
      )}
    </div>
  );
}

function FunnelBar({ label, value, maxValue, color, conversionRate }) {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  const colorClasses = {
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
    green: 'bg-green-500',
    red: 'bg-red-500',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-gray-900">{value}</span>
          {conversionRate !== undefined && (
            <span className="text-xs text-gray-500">({conversionRate}%)</span>
          )}
        </div>
      </div>
      <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${colorClasses[color]} rounded-full transition-all`}
          style={{ width: `${Math.max(percentage, 2)}%` }}
        />
      </div>
    </div>
  );
}

function DistributionBar({ label, value, maxValue, color }) {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  const colorClasses = {
    purple: 'bg-purple-500',
    blue: 'bg-blue-500',
    green: 'bg-green-500',
  };

  return (
    <div className="flex items-center gap-3">
      <div className="w-32 truncate text-sm text-gray-700" title={label}>{label}</div>
      <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${colorClasses[color]} rounded-full transition-all`}
          style={{ width: `${Math.max(percentage, 2)}%` }}
        />
      </div>
      <div className="w-10 text-right text-sm font-medium text-gray-900">{value}</div>
    </div>
  );
}

function InsightRow({ label, value, benchmark }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
      <div>
        <p className="text-sm font-medium text-gray-700">{label}</p>
        <p className="text-xs text-gray-400">{benchmark}</p>
      </div>
      <span className="text-lg font-bold text-gray-900">{value}</span>
    </div>
  );
}

function LeaderboardRow({ rank, name, primaryStat, secondaryStat, highlight }) {
  return (
    <div className={`flex items-center gap-3 p-2 rounded-lg ${highlight ? 'bg-yellow-50' : ''}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
        rank === 1 ? 'bg-yellow-100 text-yellow-700' :
        rank === 2 ? 'bg-gray-100 text-gray-600' :
        rank === 3 ? 'bg-orange-100 text-orange-600' :
        'bg-gray-50 text-gray-500'
      }`}>
        {rank}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 truncate">{name}</p>
        <p className="text-xs text-gray-500">{secondaryStat}</p>
      </div>
      <span className="text-sm font-medium text-gray-700">{primaryStat}</span>
    </div>
  );
}

// Helper functions
function calculateConversion(numerator, denominator) {
  if (!denominator || denominator === 0) return 0;
  return Math.round((numerator / denominator) * 100);
}

function formatTimeAgo(timestamp) {
  if (!timestamp) return 'Unknown';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
